/**
 * Crop une image en carré centré, redim. à 256×256 et encode en JPEG 0.80.
 * Renvoie une data-URL prête à être stockée en base.
 *
 * Logique identique à la maquette d'origine. Le résultat pèse ~10-18 Ko
 * — c'est plus que raisonnable pour stocker en BDD ou transmettre via JSON.
 */
const TARGET_SIZE = 256;
const JPEG_QUALITY = 0.80;
const MAX_INPUT_BYTES = 10 * 1024 * 1024; // 10 Mo en entrée max
const ACCEPTED = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];

export async function cropToSquareDataUrl(file: File): Promise<string> {
  // Validation rapide
  if (!file.type.startsWith('image/')) {
    throw new Error('Format non supporté — choisis une image.');
  }
  if (file.size > MAX_INPUT_BYTES) {
    throw new Error('Image trop volumineuse (max 10 Mo).');
  }

  // Quelques navigateurs (Safari iOS récent) gèrent HEIC, d'autres non.
  // On laisse passer et c'est le décodage qui plantera si besoin.
  if (file.type && !ACCEPTED.some(t => file.type.toLowerCase().includes(t.split('/')[1]))) {
    // Pas de blocage strict (les types MIME peuvent varier), juste log warning
    console.warn('Type image inhabituel :', file.type);
  }

  const dataUrl = await readFileAsDataURL(file);
  const img = await loadImage(dataUrl);

  const side = Math.min(img.naturalWidth, img.naturalHeight);
  const sx = (img.naturalWidth - side) / 2;
  const sy = (img.naturalHeight - side) / 2;

  const canvas = document.createElement('canvas');
  canvas.width = TARGET_SIZE;
  canvas.height = TARGET_SIZE;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Impossible de créer le contexte canvas.');

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, sx, sy, side, side, 0, 0, TARGET_SIZE, TARGET_SIZE);

  return canvas.toDataURL('image/jpeg', JPEG_QUALITY);
}

function readFileAsDataURL(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('Lecture du fichier impossible.'));
    reader.readAsDataURL(file);
  });
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Impossible de décoder l'image — elle est peut-être corrompue ou dans un format non géré (ex: HEIC sur Android)."));
    img.src = src;
  });
}
